import { GQLProductFragment, GQLProductSideSideType } from '@theprintbar/graphql';
import {
	BrandColours,
	ImageSize,
	backgroundImageSizes,
	getClosestPantone,
	getDesignReadySides,
	getPantoneColours,
	getPreferredContrastColor,
	getProductImageUrl,
	getSizedImageSourceSet,
	hexToPixel,
} from '@theprintbar/shared';
import type { CreativeEditorInstance } from '../CreativeEditor';
import { getBackground, getPage } from './block';
import recolourTools from './recolourTools';

export function updateBackgroundImages(editor: CreativeEditorInstance, product: GQLProductFragment, colourCode: string, domain: string) {
	if (!editor) {
		return;
	}

	const colour = product.colours?.find(({ colour }) => colour.code === colourCode)?.colour;
	if (!colour) {
		return;
	}

	getDesignReadySides(product).forEach(printSide => {
		const background = getBackground(editor, printSide.side);
		if (!background) throw 'Couldnt get background';
		const imageFill = editor.engine.block.getFill(background);
		if (!imageFill) throw 'Couldnt get image';

		//Add the current domain to the image url
		const newImage = getProductImageUrl({
			product,
			productCode: product.code,
			colourCode: colour.code,
			side: printSide.side,
			webDomain: domain,
			size: ImageSize.ProductFull,
		});

		if (!newImage) throw 'Unable to find image for colour';
		editor.engine.block.setSourceSet(
			imageFill,
			'fill/image/sourceSet',
			getSizedImageSourceSet({
				//Pixel dimensions of the requested render, not the block's design units — see page.ts.
				url: newImage,
				originalWidth: ImageSize.ProductFull,
				originalHeight: ImageSize.ProductFull,
			}),
		);
		const page = getPage(editor, printSide.side);
		const strokeColour = getPreferredContrastColor(colour.hex, BrandColours.Orange, BrandColours.Blue);
		const [r, g, b] = hexToPixel(strokeColour);
		editor.engine.block.setStrokeColor(page, { r: r / 255, g: g / 255, b: b / 255, a: 1 });

		editor.engine.block.setScopeEnabled(page, 'editor/select', false);
		editor.engine.block.setScopeEnabled(background, 'editor/select', false);

		if (printSide.side === GQLProductSideSideType.Label) {
			//Import the pantones synchronousely
			getPantoneColours().then(pantones => {
				const fillColour = getPreferredContrastColor(colour.hex, '#000000', '#FFFFFF');
				const fillPantone = getClosestPantone(hexToPixel(fillColour), pantones);
				recolourTools.recolourSide(printSide.side, {
					domain,
					editor,
					colourList: [fillPantone],
					replacements: {
						[`${fillPantone.name}`]: fillPantone,
					},
					neckOnly: true,
				});
			});
		}
	});
}
